// @ts-nocheck
/**
 * Tax exempt form handler — multipart upload to custom API endpoint.
 */
class TaxExemptForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.endpoint = this.form.dataset.apiEndpoint || '';
    this.alertEl = this.form.querySelector('[data-tax-exempt-alert]');
    this.phoneInput = this.form.querySelector('[data-phone-input]');
    this.phoneCountry = this.form.querySelector('[data-phone-country]');
    this.phoneE164Input = this.form.querySelector('[data-phone-e164]');
    this.fileInput = this.form.querySelector('[data-file-input]');
    this.dropzone = this.form.querySelector('[data-file-dropzone]');
    this.fileNameEl = this.form.querySelector('[data-file-name]');
    this.maxFileSize = Number(this.form.dataset.maxFileSize) || 10 * 1024 * 1024;

    this.setupPhoneField();
    this.setupFileUpload();
    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  setupPhoneField() {
    if (!this.phoneInput || !this.phoneCountry || !this.phoneE164Input) return;

    window.PSPhoneField?.populateCountryOptions(this.phoneCountry);

    this.phoneInput.addEventListener('input', () => {
      this.phoneInput.classList.remove('tax-exempt-field-error');
      this.syncPhoneValue(false);
    });
    this.phoneCountry.addEventListener('change', () => {
      this.phoneInput.classList.remove('tax-exempt-field-error');
      this.syncPhoneValue(false);
    });
  }

  syncPhoneValue(markInvalid) {
    return window.PSPhoneField?.syncValue({
      phoneInput: this.phoneInput,
      phoneCountry: this.phoneCountry,
      phoneE164Input: this.phoneE164Input,
      markInvalid,
      errorClass: 'tax-exempt-field-error'
    }) ?? { valid: false, e164: '' };
  }

  setupFileUpload() {
    if (!this.fileInput || !this.dropzone) return;

    this.dropzone.addEventListener('click', () => this.fileInput.click());
    this.dropzone.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.fileInput.click();
      }
    });

    this.fileInput.addEventListener('change', () => {
      this.handleFileSelection(this.fileInput.files?.[0] || null);
    });

    ['dragenter', 'dragover'].forEach((eventName) => {
      this.dropzone.addEventListener(eventName, (event) => {
        event.preventDefault();
        this.dropzone.classList.add('tax-exempt-dropzone--active');
      });
    });

    ['dragleave', 'drop'].forEach((eventName) => {
      this.dropzone.addEventListener(eventName, (event) => {
        event.preventDefault();
        this.dropzone.classList.remove('tax-exempt-dropzone--active');
      });
    });

    this.dropzone.addEventListener('drop', (event) => {
      const file = event.dataTransfer?.files?.[0] || null;
      this.handleFileSelection(file);
    });
  }

  handleFileSelection(file) {
    if (!this.fileInput || !this.dropzone) return;

    this.dropzone.classList.remove('tax-exempt-dropzone--error');

    if (!file) {
      this.fileInput.value = '';
      this.updateFileName('');
      return;
    }

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      this.fileInput.value = '';
      this.updateFileName('');
      this.showMessage('Please upload a PDF file only.', 'error');
      this.dropzone.classList.add('tax-exempt-dropzone--error');
      return;
    }

    if (file.size > this.maxFileSize) {
      this.fileInput.value = '';
      this.updateFileName('');
      this.showMessage('File is too large. Maximum size is 10MB.', 'error');
      this.dropzone.classList.add('tax-exempt-dropzone--error');
      return;
    }

    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    this.fileInput.files = dataTransfer.files;
    this.updateFileName(file.name);
    this.hideMessage();
  }

  updateFileName(name) {
    if (!this.fileNameEl) return;
    if (name) {
      this.fileNameEl.textContent = name;
      this.fileNameEl.hidden = false;
    } else {
      this.fileNameEl.textContent = '';
      this.fileNameEl.hidden = true;
    }
  }

  async onSubmit(event) {
    event.preventDefault();

    if (!this.endpoint) {
      this.showMessage('Form submission is not available yet. Please try again later.', 'error');
      return;
    }

    const submitBtn = this.form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    const phoneState = this.syncPhoneValue(true);
    if (!phoneState.valid) {
      this.showMessage('Please enter a valid phone number for the selected country.', 'error');
      if (submitBtn) submitBtn.disabled = false;
      return;
    }

    const file = this.fileInput?.files?.[0];
    if (!file) {
      this.showMessage('Please upload your completed Tax Exempt Certificate (ST-3).', 'error');
      this.dropzone?.classList.add('tax-exempt-dropzone--error');
      if (submitBtn) submitBtn.disabled = false;
      return;
    }

    const formData = new FormData();
    formData.append('name', this.form.querySelector('[name="name"]')?.value || '');
    formData.append('email', this.form.querySelector('[name="email"]')?.value || '');
    formData.append('phone', this.phoneE164Input?.value || '');
    formData.append('message', this.form.querySelector('[name="message"]')?.value || '');
    formData.append('certificate', file);

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'x-api-key': 'uoirweuorudlfjlsd'
        },
        body: formData
      });

      if (!response.ok) throw new Error('Request failed');

      const thankYouUrl = this.form.dataset.thankYouUrl || '/pages/thank-you';
      const separator = thankYouUrl.includes('?') ? '&' : '?';
      window.location.href = `${thankYouUrl}${separator}source=tax-exempt`;
    } catch (error) {
      this.showMessage(this.form.dataset.errorMessage || 'Something went wrong. Please try again later.', 'error');
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  showMessage(text, type) {
    let el = this.alertEl;
    if (!el) {
      el = document.createElement('div');
      el.className = 'tax-exempt-form-alert';
      el.setAttribute('role', 'alert');
      el.setAttribute('aria-live', 'polite');
      el.setAttribute('data-tax-exempt-alert', '');
      this.form.prepend(el);
      this.alertEl = el;
    }
    el.hidden = false;
    el.className = `tax-exempt-form-alert tax-exempt-form-alert--${type}`;
    el.textContent = text;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  hideMessage() {
    if (this.alertEl) {
      this.alertEl.hidden = true;
    }
  }
}

if (!customElements.get('tax-exempt-form')) {
  customElements.define('tax-exempt-form', TaxExemptForm);
}
